const cleanText = value => String(value || '').replace(/\s+/g, ' ').trim()

const editMode = candidate => {
  const mode = String(candidate?.mode || '').toLowerCase()
  return mode === 'apply' || mode === 'suggest' ? mode : undefined
}

const editTitle = candidate => {
  if (candidate?.display?.title) return candidate.display.title
  if (candidate?.title) return candidate.title
  if (candidate?.insert?.text) return candidate.insert.text
  if (candidate?.text) return candidate.text
  if (candidate?.replacement) return candidate.replacement
  const op = (candidate?.ops || candidate?.patch?.ops || [])[0]
  if (op?.op === 'replace') return op.text || op.replace || op.to || ''
  return ''
}

const editSubtitle = candidate => {
  if (candidate?.display?.subtitle) return candidate.display.subtitle
  const mode = editMode(candidate)
  if (mode === 'apply') return 'Apply'
  if (mode === 'suggest') return 'Suggestion'
  return candidate?.source?.channel || candidate?.type || ''
}

const replacementOpFromCandidate = candidate => {
  const find = candidate?.find || candidate?.from || ''
  const text = candidate?.text || candidate?.replacement || candidate?.replace || candidate?.to || candidate?.candidate || ''
  if (!find || !text) return null
  return {
    op: 'replace',
    scope: candidate?.scope || 'beforeCursor',
    find,
    text
  }
}

const insertFromCandidate = candidate => {
  const text = candidate?.insert?.text || candidate?.text || candidate?.candidate || candidate?.replacement || ''
  if (!text) return null
  return {
    text,
    mode: candidate?.insert?.mode || candidate?.insertMode || 'insertAtCursor'
  }
}

export const normalizeKeyboardEditCandidate = candidate => {
  if (typeof candidate === 'string') {
    return {
      type: 'insert',
      insert: {
        text: candidate,
        mode: 'insertAtCursor'
      },
      display: {
        title: candidate,
        subtitle: ''
      }
    }
  }
  if (!candidate || typeof candidate !== 'object') return candidate
  const mode = editMode(candidate)
  const rawType = String(candidate.type || candidate.kind || '').toLowerCase()
  if (candidate.patch || rawType === 'patch') {
    return {
      ...candidate,
      mode,
      type: 'patch',
      display: {
        ...candidate.display,
        title: editTitle(candidate),
        subtitle: editSubtitle(candidate)
      }
    }
  }
  if (candidate.insert || rawType === 'insert') {
    const insert = insertFromCandidate(candidate)
    if (!insert) return candidate
    return {
      ...candidate,
      mode,
      type: 'insert',
      insert,
      display: {
        ...candidate.display,
        title: editTitle({ ...candidate, insert }),
        subtitle: editSubtitle(candidate)
      }
    }
  }
  const ops = Array.isArray(candidate.ops) ? candidate.ops : []
  if (rawType === 'replace' || ops.length) {
    const patchOps = ops.length ? ops : [replacementOpFromCandidate(candidate)].filter(Boolean)
    if (!patchOps.length) return candidate
    return {
      ...candidate,
      mode,
      type: 'patch',
      patch: {
        format: candidate.format || 'operations',
        scope: candidate.scope || 'beforeCursor',
        ops: patchOps
      },
      display: {
        ...candidate.display,
        title: editTitle({ ...candidate, ops: patchOps }),
        subtitle: editSubtitle(candidate)
      }
    }
  }
  return candidate
}

export const normalizeKeyboardCompletions = completions => {
  return (completions || []).map(normalizeKeyboardEditCandidate)
}

export const keyboardCandidateTitle = candidate => {
  const normalized = normalizeKeyboardEditCandidate(candidate)
  return normalized?.display?.title || normalized?.insert?.text || editTitle(normalized) || ''
}

export const keyboardCandidateSubtitle = candidate => {
  const normalized = normalizeKeyboardEditCandidate(candidate)
  return normalized?.display?.subtitle || normalized?.source?.channel || normalized?.mode || normalized?.type || ''
}

export const keyboardCompletionSignature = completions => {
  return JSON.stringify(normalizeKeyboardCompletions(completions).map(x => ({
    mode: x?.mode,
    type: x?.type,
    title: x?.display?.title,
    subtitle: x?.display?.subtitle,
    insert: x?.insert?.text,
    patch: x?.patch?.ops
  })))
}

export const formatCaretInsertionText = (
  text,
  {
    whitespaceBefore = false,
    whitespaceAfter = false,
    preserveLeftBoundary = false
  } = {}
) => {
  let result = String(text || '')
  if (preserveLeftBoundary) {
    return !whitespaceAfter && !/\s$/u.test(result)
      ? result + ' '
      : result
  }
  const insideWord = !whitespaceBefore && !whitespaceAfter
  if (!insideWord) {
    if (!whitespaceBefore && !result.startsWith("'")) {
      result = ' ' + result
    }
    if (!whitespaceAfter) {
      result += ' '
    }
  }
  return result
}

export const applyPatchCandidateToText = (text, candidate, logPrefix = '[intellikey-autocomplete:patch]') => {
  const normalized = normalizeKeyboardEditCandidate(candidate)
  const ops = normalized?.patch?.ops || []
  return ops.reduce((current, op) => {
    if (op?.op === 'insert') {
      const insertion = op.text || op.insert || ''
      if (!insertion) {
        console.log(`${logPrefix}:skip`, { op, reason: 'empty-insert' })
        return current
      }
      console.log(`${logPrefix}:insert`, { op })
      return current + insertion
    }
    if (op?.op !== 'replace' || !op.find) {
      console.log(`${logPrefix}:skip`, { op, reason: 'unsupported-op-or-empty-find' })
      return current
    }
    const i = current.lastIndexOf(op.find)
    if (i < 0) {
      console.log(`${logPrefix}:miss`, {
        op: op?.op,
        findLength: String(op?.find || '').length,
        textLength: current.length
      })
      return current
    }
    console.log(`${logPrefix}:hit`, { op, index: i })
    return current.slice(0, i) + (op.text || op.replace || op.to || '') + current.slice(i + op.find.length)
  }, text)
}

export const autocorrectApplyEdits = response => {
  return normalizeKeyboardCompletions(response?.edits).filter(candidate => candidate?.mode === 'apply')
}

export const autocorrectSuggestEdits = response => {
  const edits = normalizeKeyboardCompletions(response?.edits).filter(candidate => candidate?.mode === 'suggest')
  const suggestions = normalizeKeyboardCompletions(response?.suggestions || [])
  return [...edits, ...suggestions]
}

export const correctedTextFromAutocorrectResponse = (text, response) => {
  if (typeof response === 'string') return response
  const corrected = response?.correctedText || response?.corrected
  if (typeof corrected === 'string' && corrected && corrected !== text) return corrected
  const applyEdits = autocorrectApplyEdits(response)
  if (applyEdits.length) {
    return applyEdits.reduce((current, candidate) => {
      if (candidate?.type === 'patch') return applyPatchCandidateToText(current, candidate, '[intellikey-autocorrect:patch]')
      if (candidate?.type === 'insert') return current + (candidate?.insert?.text || '')
      return current
    }, text)
  }
  const replacement = cleanText(response?.correct || response?.complete)
  if (replacement && !/\s/.test(replacement)) {
    const match = text.match(/(\S+)(\s*)$/)
    if (match) {
      const token = match[1]
      const trailing = match[2] || ''
      return text.slice(0, text.length - token.length - trailing.length) + replacement + trailing
    }
  }
  return text
}
